import { get } from 'lodash';
import ApiService from './api-service';
import ApiError from '../util/ApiError';

const AccountService = {
  async getUserOrganisations() {
    const body = {
      query: `
        query {
          user {
            organisations {
              Name
              name
              ShortCode
            }
          }
        }
      `,
    };

    const data = await ApiService.meEndpoint().post('/', body);
    return get(data, 'user.organisations', []).sort((o1, o2) => {
      const o1Name = get(o1, 'name', '');
      const o2Name = get(o2, 'name', '');
      const comparison = o1Name.localeCompare(o2Name);
      if (comparison > 0) {
        return 1;
      }
      if (comparison < 0) {
        return -1;
      }
      return 0;
    });
  },
  async getUserInfo() {
    const body = {
      query: `
        query {
          user {
            emails {
              address
              verified
            }
            _id
            hasTOTP
          }
        }
      `,
    };

    const data = await ApiService.meEndpoint().post('/', body);
    let user = get(data, 'user', {});
    user.hasVerifiedEmail = user.emails && user.emails.some(e => e.verified);
    return user;
  },
  async getNotificationSettings() {
    const body = {
      query: `
        query {
          user {
            notificationsSettings {
              organisation {
                ShortCode
                name
              }
              configuration {
                type
                email
                push_notification
              }
            }
          }
        }
      `,
    };

    const data = await ApiService.meEndpoint().post('/', body);
    return get(data, 'user');
  },
  async updateNotificationSetting({ shortCode, type, email }) {
    const body = {
      query: `mutation updateNotificationSetting($shortCode: String!, $type: String!, $email: Boolean!) {
        updateNotificationSetting(ShortCode: $shortCode, configurationType: $type, emailType: $email)
      }`,
      variables: {
        shortCode,
        type,
        email,
      },
    };

    const data = await ApiService.meEndpoint().post('/', body);
    return data;
  },
  async updateAllNotificationSettings(enable) {
    const disableQuery = `mutation {
      disableAllNotificationSettings
    }`;
    const enableQuery = `mutation {
      enableAllNotificationSettings
    }`;
    const body = {
      query: enable ? enableQuery : disableQuery,
    };

    const data = await ApiService.meEndpoint().post('/', body);
    return data[enable ? 'enableAllNotificationSettings' : 'disableAllNotificationSettings'];
  },
  async setOverlay(overlay, show) {
    const body = {
      query: `mutation showOverlay($overlay: String, $show: Boolean!) {
        showOverlay(overlay: $overlay, show: $show)
      }`,
      variables: {
        overlay,
        show,
      },
    };
    await ApiService.meEndpoint().post('/', body);
  },
  async sendSupportMessage({ message, subject }) {
    const body = {
      query: `mutation sendSupportMessage($message: String!, $subject: String!) {
        sendSupportMessage(subject: $subject, message: $message)
      }`,
      variables: {
        message,
        subject,
      },
    };

    const data = await ApiService.meEndpoint().post('/', body);
    if (!data.sendSupportMessage)
      throw new ApiError('Failed to send message to support', { log: false });
    return true;
  },
  async createChargebeePortalSession({ shortCode }) {
    const body = {
      query: `mutation createChargebeePortalSession($shortCode: String!) {
          createChargebeePortalSession(ShortCode: $shortCode)
        }
      `,
      variables: {
        shortCode,
      },
    };

    const data = await ApiService.meEndpoint().post('/', body);
    return data.createChargebeePortalSession;
  },

  async resendVerifyEmailEmail(email) {
    const body = {
      query: `mutation($email: String!) {
          resendVerifyEmailEmail(email: $email)
        }
      `,
      variables: {
        email,
      },
    };

    try {
      await ApiService.meEndpoint({ logoutWhenUnauthorized: false }).post('/', body);
    } catch (err) {
      if (err?.response?.status !== 403) {
        throw err;
      }

      await ApiService.baseEndpoint({ logoutWhenUnauthorized: false }).post('/', body);
    }
  },

  async verifyEmail({ email, token }) {
    const body = {
      query: `mutation($email: String!, $token: String!) {
          verifyEmail(email: $email, token: $token) {
            shouldSetPassword
            verified
            token
          }
        }
      `,
      variables: {
        email,
        token,
      },
    };

    return (await ApiService.baseEndpoint().post('/', body)).verifyEmail;
  },

  async getCurrentUser() {
    const body = {
      query: `
        query {
          user {
            _id
            emails {
              address
              verified
            }
            permissions {
              global
              organisation {
                roles
                orgShortCode
              }
            }
            profile {
              firstName
              lastName
              features {
                intuitAuthMerged
                isPartnerHubUser
                receivablesForecastTrialEndDate
              }
            }
            overlays {
              collector
            }
            organisations {
              Name
              name
              ShortCode
              OrganisationID
            }
            latestAccess {
              organisation
            }
            createdAt
            hasChargebeeCustomer
            hasTOTP
            needsTOTP
          }
        }
      `,
    };

    const data = await ApiService.meEndpoint().post('/', body);
    return get(data, 'user', {});
  },

  async updateProfile({ firstName, lastName, shortCode }) {
    const body = {
      query: `mutation($firstName: String!, $lastName: String!) {
        updateProfile(firstName: $firstName, lastName: $lastName)
      }`,
      variables: {
        firstName,
        lastName,
      },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },

  async submitOnboardingSurvey({
    shortCode,
    firstName,
    lastName,
    phoneNumber,
    phoneCountryCode,
    isAccountingFirm,
    accountingSoftware,
    howDidYouHear,
  }) {
    const body = {
      query: `mutation submitOnboardingSurvey(
        $shortCode: String!,
        $firstName: String,
        $lastName: String,
        $phoneNumber: String,
        $phoneCountryCode: String,
        $isAccountingFirm: String,
        $accountingSoftware: String,
        $howDidYouHear: String
      ) {
        submitOnboardingSurvey(
          shortCode: $shortCode,
          firstName: $firstName,
          lastName: $lastName,
          phoneNumber: $phoneNumber,
          phoneCountryCode: $phoneCountryCode,
          isAccountingFirm: $isAccountingFirm,
          accountingSoftware: $accountingSoftware,
          howDidYouHear: $howDidYouHear
        )
      }`,
      variables: {
        shortCode,
        firstName,
        lastName,
        phoneNumber,
        phoneCountryCode,
        isAccountingFirm,
        accountingSoftware,
        howDidYouHear,
      },
    };
    const data = await ApiService.meEndpoint().post('/', body);
    return data.submitOnboardingSurvey;
  },
};

export default AccountService;
